import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const STEP_LABELS = ['Add source', 'Review', 'Save'];

function RecipeAddWizard({
    step,
    input,
    onInputChange,
    onExtract,
    loading,
    error,
    recipe,
    title,
    onTitleChange,
    onSave,
    saving,
    saveMessage,
    tags,
    onToggleTag,
    onAddTag,
    onStartOver,
    titleInputRef,
}) {
    const confidence = recipe?.extractionMeta?.confidence || 'unknown';
    const isLowConfidence = confidence === 'low';
    const [tagInput, setTagInput] = useState('');

    return (
        <section className="panel recipe-wizard" aria-labelledby="recipe-wizard-title">
            <h2 id="recipe-wizard-title">Add recipe</h2>
            <p className="text-muted">Use this simple 3-step flow to add a recipe with clear, large actions.</p>

            <ol className="wizard-steps" aria-label="Add recipe progress">
                {STEP_LABELS.map((label, index) => (
                    <li key={label}>
                        <button
                            type="button"
                            className={`wizard-step-chip ${step === index ? 'is-active' : ''}`}
                            onClick={() => {
                                if (index < step) {
                                    onStartOver(index);
                                }
                            }}
                            disabled={index > step}
                            aria-current={step === index ? 'step' : undefined}
                        >
                            <span className="wizard-step-number" aria-hidden="true">{index + 1}</span>
                            <span>{label}</span>
                        </button>
                    </li>
                ))}
            </ol>

            {step === 0 && (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        onExtract(e);
                    }}
                    className="extractor-form wizard-panel"
                    aria-describedby="source-help"
                >
                    <h3 className="wizard-panel-title">Add source</h3>
                    <p id="source-help" className="text-muted">Paste a recipe link or recipe text. We will organize it for you.</p>
                    <label htmlFor="recipeInput" className="form-label">Recipe source</label>
                    <textarea
                        id="recipeInput"
                        aria-label="Recipe source text or URL"
                        className="form-control wizard-input"
                        rows="7"
                        placeholder="Paste a recipe website link or full recipe text"
                        value={input}
                        onChange={(e) => onInputChange(e.target.value)}
                        required
                    />
                    <div className="extractor-actions wizard-actions">
                        <button type="submit" className="btn btn-primary wizard-btn" disabled={loading || !input.trim()}>
                            {loading ? 'Getting recipe…' : 'Next: Review recipe'}
                        </button>
                    </div>
                </form>
            )}

            {step === 1 && recipe && (
                <form
                    className="wizard-panel"
                    onSubmit={(e) => {
                        e.preventDefault();
                        onSave();
                    }}
                >
                    <h3 className="wizard-panel-title">Review</h3>

                    <div className={`alert ${isLowConfidence ? 'alert-warning' : 'alert-secondary'}`} role="status">
                        <strong>Extraction confidence:</strong> {confidence}.
                        {isLowConfidence ? ' Please review ingredients and steps before saving.' : ' Looks good. You can edit the title here before saving.'}
                        {Array.isArray(recipe.extractionMeta?.warnings) && recipe.extractionMeta.warnings.length > 0 && (
                            <ul className="wizard-warning-list">
                                {recipe.extractionMeta.warnings.map((warning, index) => (
                                    <li key={`${warning}-${index}`}>{warning}</li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <label htmlFor="recipeTitle" className="form-label">Recipe title</label>
                    <input
                        ref={titleInputRef}
                        id="recipeTitle"
                        type="text"
                        className="form-control wizard-input"
                        placeholder="Give your recipe a clear name"
                        value={title}
                        onChange={(e) => onTitleChange(e.target.value)}
                    />

                    <div className="mt-3">
                        <h4>Tags</h4>
                        <p className="text-muted mb-2">Suggested tags are pre-selected. Tap to remove or add your own.</p>
                        <div className="wizard-tag-list">
                            {(recipe.suggestedTags || []).map((tag) => {
                                const selected = (tags || []).includes(tag);
                                return (
                                    <button
                                        key={tag}
                                        type="button"
                                        className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => onToggleTag(tag)}
                                        aria-pressed={selected}
                                    >
                                        {tag}
                                    </button>
                                );
                            })}
                            {(!recipe.suggestedTags || recipe.suggestedTags.length === 0) && (
                                <span className="text-muted">No suggestions for this recipe yet.</span>
                            )}
                        </div>
                        <div className="tag-input-group mt-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Add custom tag"
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        onAddTag(tagInput);
                                        setTagInput('');
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                    onAddTag(tagInput);
                                    setTagInput('');
                                }}
                            >
                                Add tag
                            </button>
                        </div>
                        {(tags || []).length > 0 && (
                            <div className="wizard-selected-tags mt-2">
                                <span className="wizard-selected-tags__label">Selected tags</span>
                                <div className="wizard-tag-list">
                                {tags.map((tag) => (
                                    <button
                                        key={`selected-${tag}`}
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => onToggleTag(tag)}
                                    >
                                        {tag} ×
                                    </button>
                                ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="wizard-review-grid">
                        <div>
                            <h4>Ingredients</h4>
                            {recipe.ingredients.length > 0 ? (
                                <ul className="list-group">
                                    {recipe.ingredients.map((item, index) => (
                                        <li key={`ingredient-${index}`} className="list-group-item">{item}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-muted">No ingredients found. You can still save and edit later.</p>
                            )}
                        </div>

                        <div>
                            <h4>Instructions</h4>
                            {recipe.instructions.length > 0 ? (
                                <ol className="list-group">
                                    {recipe.instructions.map((item, index) => (
                                        <li key={`instruction-${index}`} className="list-group-item">{item}</li>
                                    ))}
                                </ol>
                            ) : (
                                <p className="text-muted">No instructions found. Try another source if needed.</p>
                            )}
                        </div>

                        <div>
                            <h4>Notes</h4>
                            {recipe.notes.length > 0 ? (
                                <ul className="list-group">
                                    {recipe.notes.map((item, index) => (
                                        <li key={`note-${index}`} className="list-group-item">{item}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-muted">No notes found. You can add personal notes later in the recipe editor.</p>
                            )}
                        </div>
                    </div>

                    {saveMessage && (
                        <div className="alert alert-info mt-3" role="status">{saveMessage}</div>
                    )}

                    <div className="extractor-actions wizard-actions">
                        <button type="button" className="btn btn-outline-secondary wizard-btn" onClick={() => onStartOver(0)}>
                            Back to source
                        </button>
                        <button type="submit" className="btn btn-success wizard-btn" disabled={saving || !title.trim()}>
                            {saving ? 'Saving…' : 'Save recipe'}
                        </button>
                    </div>
                </form>
            )}

            {step === 2 && (
                <div className="wizard-panel" role="status" aria-live="polite">
                    <h3 className="wizard-panel-title">Save</h3>
                    <p className="wizard-success-text">Your recipe was saved successfully.</p>
                    <p className="text-muted">Choose your next step.</p>
                    <div className="wizard-big-actions">
                        <button type="button" className="btn btn-primary wizard-btn wizard-btn--big" onClick={() => onStartOver(0)}>
                            Add another recipe
                        </button>
                        <Link to="/saved-recipes" className="btn btn-outline-primary wizard-btn wizard-btn--big">
                            Open saved recipes
                        </Link>
                    </div>
                </div>
            )}

            {error && (
                <div className="alert alert-danger mt-3" role="alert">
                    {error}
                </div>
            )}

            <div aria-live="polite" className="sr-only">
                {loading ? 'Extracting recipe...' : step === 1 ? 'Review step' : step === 2 ? 'Save step' : 'Add source step'}
            </div>
        </section>
    );
}

export default RecipeAddWizard;
